/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as v from 'valibot';

// Build-time-baked values (identity of the image, same for all channels).
const buildInfoSchema = v.object({
	PUBLIC_BUILD_SHA: v.nullish(v.string(), 'dev'),
	PUBLIC_BUILD_NUMBER: v.nullish(v.pipe(v.string(), v.transform(Number), v.number()), '0'),
	PUBLIC_BUILD_TIMESTAMP: v.nullish(v.pipe(v.string(), v.transform(Number), v.number()), '0'),
});

// Runtime values — read from `window.__FLUXER_CONFIG__`, which the app proxy
// injects into index.html based on the pod's env vars at request time. This
// lets a single app-proxy image be deployed to any release channel without
// rebuilding; the channel/endpoints are determined by the deployment, not
// the bundle.
const runtimeConfigSchema = v.object({
	PUBLIC_RELEASE_CHANNEL: v.nullish(v.picklist(['stable', 'canary', 'nightly']), 'nightly'),
	PUBLIC_BOOTSTRAP_API_ENDPOINT: v.nullish(v.string(), '/api'),
	PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT: v.nullish(v.string()),
});

const buildInfo = v.parse(buildInfoSchema, {
	PUBLIC_BUILD_SHA: import.meta.env.PUBLIC_BUILD_SHA,
	PUBLIC_BUILD_NUMBER: import.meta.env.PUBLIC_BUILD_NUMBER,
	PUBLIC_BUILD_TIMESTAMP: import.meta.env.PUBLIC_BUILD_TIMESTAMP,
});

// Fall back to `import.meta.env` when the runtime injection is absent — the
// dev server doesn't run the proxy's HTML rewrite, so `window.__FLUXER_CONFIG__`
// isn't defined there.
const runtimeSource =
	typeof window !== 'undefined' && (window as {__FLUXER_CONFIG__?: Record<string, string>}).__FLUXER_CONFIG__
		? (window as unknown as {__FLUXER_CONFIG__: Record<string, string>}).__FLUXER_CONFIG__
		: {
				PUBLIC_RELEASE_CHANNEL: import.meta.env.PUBLIC_RELEASE_CHANNEL,
				PUBLIC_BOOTSTRAP_API_ENDPOINT: import.meta.env.PUBLIC_BOOTSTRAP_API_ENDPOINT,
				PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT: import.meta.env.PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT,
			};

const runtime = v.parse(runtimeConfigSchema, runtimeSource);

export default {
	PUBLIC_BUILD_SHA: buildInfo.PUBLIC_BUILD_SHA,
	PUBLIC_BUILD_NUMBER: buildInfo.PUBLIC_BUILD_NUMBER,
	PUBLIC_BUILD_TIMESTAMP: buildInfo.PUBLIC_BUILD_TIMESTAMP,
	PUBLIC_RELEASE_CHANNEL: runtime.PUBLIC_RELEASE_CHANNEL,
	PUBLIC_BOOTSTRAP_API_ENDPOINT: runtime.PUBLIC_BOOTSTRAP_API_ENDPOINT,
	PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT:
		runtime.PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT ?? runtime.PUBLIC_BOOTSTRAP_API_ENDPOINT,
};
